import { env } from "@/lib/env";
import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
  }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Register what happens when the backend rejects our bearer token (401):
 * the session was revoked, the account deleted, or the JWT is otherwise
 * no longer accepted. `AuthProvider` registers a local sign-out, which
 * flips `session` to null so `RequireAuth` redirects to `/login`.
 * Returns an unregister function.
 */
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler,
): () => void {
  unauthorizedHandler = handler;
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null;
  };
}

type RequestOptions = Omit<RequestInit, "headers" | "body"> & {
  body?: unknown;
  headers?: Record<string, string>;
};

/**
 * Typed fetch wrapper to the FastAPI backend. Attaches the Supabase JWT
 * (when present) as `Authorization: Bearer <token>`.
 */
export async function api<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const { body, headers, ...rest } = opts;

  const authHeader: Record<string, string> = {};
  if (isSupabaseConfigured()) {
    try {
      const {
        data: { session },
      } = await getSupabase().auth.getSession();
      if (session?.access_token) {
        authHeader["Authorization"] = `Bearer ${session.access_token}`;
      }
    } catch {
      // Session unavailable — proceed without auth header.
    }
  }

  const res = await fetch(`${env.API_BASE_URL}${path}`, {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeader,
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    // Only a request that *carried* a token proves the session is dead; an
    // anonymous call to an auth-only route is not a reason to sign out.
    if (res.status === 401 && authHeader["Authorization"]) {
      unauthorizedHandler?.();
    }
    // Read the body once: calling .text() after a failed .json() throws
    // "Body is unusable" instead of surfacing the ApiError.
    const raw = await res.text().catch(() => "");
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw;
    }
    throw new ApiError(res.status, parsed);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
